import { useId, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { AreaTabs, PAYROLL_TABS } from "@/components/AreaTabs";
import { StateMessage } from "@/components/StateMessage";
import { PayRunPieceSection } from "@/components/PayRunPieceSection";
import { problemMessage } from "@/api/client";
import { usePayRuns } from "@/hooks/usePayRuns";
import { usePayRunPiece } from "@/hooks/usePayRunPiece";
import type { HoursReport, PayRun, PayRunFinalizeLineWithPiece, PayrollEmployee } from "@/api/types";
import { formatHours, formatMoney } from "@/lib/format";
import "./PayRuns.css";

/**
 * ef-034 — `/apply`, `/runs`, `/report` and `/finalize` (ef-014/ef-015/ef-027) turned into
 * one owner screen: pick a period, apply its approved hours onto a run, finalize the run
 * into money, and look back at the runs that came before it.
 *
 * A payroll SUB-screen, same nav treatment as Approved hours/Saved places beside it in
 * Payroll's "More payroll screens" nav — see App.tsx's routing note and Payroll.tsx.
 *
 * <p>
 * <b>ONE ENTITY.</b> Same simplifying assumption every other payroll screen in this build
 * already makes (`usePayrollRoster`'s "primary" scope, `BusinessEntity`'s own remarks that
 * the demo seeds one entity per tenant) — see usePayRuns.ts.
 * </p>
 *
 * <p>
 * <b>PER-EMPLOYEE MONEY: a live `POST /finalize` response wins, `run.lines` is the
 * fallback (ef-035).</b> `GET /runs` and `POST /apply` now carry each line's own
 * gross/FICA/withholding/net too — null until finalized, all six together (0030's
 * all-or-nothing) — so a run finalized in an earlier session (or by an earlier test run
 * today — the API's own `ApplyApprovedHoursAsync` refuses to re-apply over a finalized
 * period) still shows a real per-employee breakdown after a reload, not a "—" placeholder.
 * The live `finalizeResult` is preferred only because it is the freshest read of the two,
 * never because `run.lines` lacks the figures. See `usePayRuns.finalizeResult`'s own doc
 * comment.
 * </p>
 */
export interface PayRunsProps {
  /** ef-047-4 — the "Print check" row action navigates to
   *  `/payroll/runs/{runId}/print/{lineId}`, same `navigate` prop shape every other
   *  cross-page link in this app already takes (H2a/YearEnd/Hours/…). */
  onNavigate: (path: string) => void;
}

export function PayRuns({ onNavigate }: PayRunsProps) {
  const payRuns = usePayRuns();
  const {
    status,
    error,
    navigating,
    entity,
    employees,
    period,
    periodReport,
    run,
    runState,
    history,
    finalizeResult,
  } = payRuns;

  // ef-060-8 — the piece tickets behind this run. Re-read when the run changes state (finalize turns
  // "added when you finalize" into "included in this run"); a voided run has released its tickets.
  const piece = usePayRunPiece(run && runState !== "voided" ? run.id : null, runState);

  const [actionError, setActionError] = useState<string | null>(null);
  const [finalizing, setFinalizing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [posting, setPosting] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [voiding, setVoiding] = useState(false);
  const voidReasonId = useId();

  // ef-033 — "finalized" and "posted" both mean this run's money is real and fixed;
  // posting computes no new figures (PayRunPostEndpoints.cs's own remark), so the money
  // table/Assumptions disclosure that a finalized run earns must not disappear the moment
  // `runState` moves on to "posted".
  const hasBeenFinalized = runState === "finalized" || runState === "posted";

  const hasApprovableHours = (periodReport?.lines ?? []).some((l) => l.approvedHours > 0);
  // A posted run can never be voided back open (ef-029b's 409, until a reversal lands —
  // ef-036), so Apply must stay refused exactly like it does on a plain finalized run;
  // `hasBeenFinalized` (not a bare `runState === "finalized"`) is what keeps this true
  // once posting moves `runState` on to "posted".
  const applyDisabled = hasBeenFinalized || !hasApprovableHours || applying;

  // ef-037b (ADR 0009 §4) — "the pay run's action row never shows two primary actions."
  // Apply itself is never unmounted (`pay-runs.spec.ts`/`pay-runs-void.spec.ts` both read it
  // — disabled and re-enabled — across every state, so it cannot simply disappear the way
  // Finalize/Post do), but a LATER step being offered in the same row means Apply is no
  // longer the action this screen wants a farmer's eye drawn to.
  //
  // DERIVED FROM THE SAME BOOLEANS THE BUTTONS THEMSELVES RENDER ON, not a second,
  // independently-written `runState` comparison. A parallel check (`runState !== "applied"
  // && runState !== "finalized"`) is only correct as long as it is kept byte-for-byte in
  // sync with the JSX gates below by hand — row-actions.spec.ts's own "never two primaries"
  // canary caught exactly that drifting on the shared demo tenant. `showFinalize`/`showPost`
  // are that single source of truth: the JSX below reads them instead of re-deriving its own
  // condition, so there is no second place left for the two to disagree.
  const showFinalize = runState === "applied";
  const showPost = runState === "finalized";
  const applyIsPrimary = !showFinalize && !showPost;

  async function handleApply(): Promise<void> {
    setActionError(null);
    setApplying(true);
    try {
      await payRuns.apply();
    } catch (err) {
      setActionError(problemMessage(err, "Could not apply approved hours."));
    } finally {
      setApplying(false);
    }
  }

  async function handleFinalize(): Promise<void> {
    setActionError(null);
    setFinalizing(true);
    try {
      await payRuns.finalize();
    } catch (err) {
      setActionError(problemMessage(err, "Could not finalize this run."));
    } finally {
      setFinalizing(false);
    }
  }

  async function handlePost(): Promise<void> {
    setActionError(null);
    setPosting(true);
    try {
      await payRuns.post();
    } catch (err) {
      setActionError(problemMessage(err, "Could not post this run."));
    } finally {
      setPosting(false);
    }
  }

  async function handleVoid(): Promise<void> {
    setActionError(null);
    setVoiding(true);
    try {
      await payRuns.voidRun(voidReason);
      setVoidReason("");
    } catch (err) {
      setActionError(problemMessage(err, "Could not void this run."));
    } finally {
      setVoiding(false);
    }
  }

  // Derived, never stored (persona: state-management guidelines) — recomputed from the
  // roster's own rates every render rather than parsed out of the 400's comma-joined
  // sentence, so the inline fix-up forms below stay correct even before Finalize has ever
  // been clicked.
  const missingRateEmployees: PayrollEmployee[] = run
    ? run.lines
        .map((l) => employees.find((e) => e.id === l.employeeId))
        .filter(
          (e): e is PayrollEmployee => !!e && e.payBasis === "hourly" && e.hourlyRate == null,
        )
    : [];

  // ef-035: GET /runs (and POST /apply) now carry each line's own money — null until
  // finalized, all six together (0030's all-or-nothing) — so a RELOADED finalized run no
  // longer needs the live `finalizeResult` to show a per-employee breakdown. The live
  // response still wins when both exist (same "a live response always wins" rule the
  // FALLBACK_ASSUMPTIONS comment below states) since it is the freshest read; `run.lines`
  // is the fallback for a run finalized before this page session began.
  const financeForRun: PayRunFinalizeLineWithPiece[] | null =
    finalizeResult && run && finalizeResult.id === run.id
      ? finalizeResult.lines
      : run && hasBeenFinalized && run.lines.every((l) => l.gross !== null)
        ? run.lines.map((l) => ({
            employeeId: l.employeeId,
            employeeNumber: l.employeeNumber,
            employeeName: l.employeeName,
            gross: l.gross!,
            ficaEmployee: l.ficaEmployee!,
            ficaEmployer: l.ficaEmployer!,
            federalWh: l.federalWh!,
            stateWh: l.stateWh!,
            net: l.net!,
            piecePay: l.piecePay,
          }))
        : null;

  return (
    <main className="pay-runs">
      <PageHeader
        title="Pay runs"
        subtitle="Apply approved hours onto a run, finalize it into money, and look back at what came before."
      />

      <AreaTabs area="/payroll" tabs={PAYROLL_TABS} label="Payroll" />

      {status === "loading" && <StateMessage tone="loading" title="Loading pay runs…" />}

      {status === "error" && (
        <StateMessage tone="error" title="Could not load pay runs." detail={error ?? undefined} />
      )}

      {status === "ready" && entity && period && (
        <>
          <ScheduleSection
            entityName={entity.name}
            paySchedule={entity.paySchedule}
            period={period}
            navigating={navigating}
            onPrevious={() => void payRuns.goToPreviousPeriod()}
            onNext={() => void payRuns.goToNextPeriod()}
          />

          <section
            className="pay-runs__run"
            aria-labelledby="run-heading"
            data-state={runState}
            aria-busy={navigating || undefined}
          >
            {/* Straight apostrophe, deliberately — the DOM contract's own
                `runRegion` locator matches `/this period's run/i` against a literal U+0027,
                not the curly U+2019 this app otherwise prefers in prose. */}
            <h2 id="run-heading" className="pay-runs__section-title">
              This period's run
            </h2>

            {(error || actionError) && (
              <p className="pay-runs__error" role="alert">
                {actionError ?? error}
              </p>
            )}

            <div className="pay-runs__actions">
              <button
                type="button"
                className={`btn ${applyIsPrimary ? "btn--primary" : "btn--ghost"}`}
                disabled={applyDisabled}
                onClick={() => void handleApply()}
              >
                Apply approved hours
                {/* aria-hidden: the accessible name stays exactly "Apply approved hours"
                    (the DOM contract's own anchored locator) — this is a VISIBLE-only
                    addendum, same pattern as an icon+badge button elsewhere in this app. */}
                {hasBeenFinalized && <span aria-hidden="true"> — finalized</span>}
                {applying && <span aria-hidden="true"> — applying…</span>}
              </button>

              {showFinalize && (
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={finalizing}
                  onClick={() => void handleFinalize()}
                >
                  {finalizing ? "Finalizing…" : "Finalize"}
                </button>
              )}

              {/* ef-033 — offered ONLY on a still-unposted finalized run; disappears the
                  moment `runState` becomes "posted", same convention Finalize already
                  follows once a run leaves "applied". */}
              {showPost && (
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={posting}
                  onClick={() => void handlePost()}
                >
                  {posting ? "Posting…" : "Post to ledger"}
                </button>
              )}
            </div>

            {/* ef-030/ef-033 — undo a mistaken finalize, OR void a run that has already
                been posted (refused with 409 by the API — ef-029b — until a reversing
                entry exists; the copy below says so up front rather than only after a
                failed attempt). Disappears the moment `runState` becomes "voided", same
                convention as Finalize disappearing once a run is finalized. */}
            {(runState === "finalized" || runState === "posted") && (
              <div className="pay-runs__void">
                {runState === "posted" && (
                  <p className="pay-runs__posted-note">
                    Voiding does not touch the posted ledger entry — it stays on the
                    books until a separate reversing entry is filed.
                  </p>
                )}
                <label htmlFor={voidReasonId}>Void reason</label>
                <input
                  id={voidReasonId}
                  type="text"
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                  disabled={voiding}
                />
                <button
                  type="button"
                  className="btn btn--danger"
                  disabled={voiding}
                  onClick={() => void handleVoid()}
                >
                  {voiding ? "Voiding…" : "Void this run"}
                </button>
              </div>
            )}

            {/* ef-033 — the live-region text is the bare state word ONLY. The void reason
                is free text an owner typed (ef-030) and must not be folded into the SAME
                `role="status"` element: `postedBadge`'s own locator
                (`getByRole("status").filter({ hasText: /posted/i })`) matches on substring,
                and a reason like "already posted this by mistake" would make a VOIDED run
                read as posted to that locator. Keeping the reason in a separate, non-status
                paragraph is also the better screen-reader behavior on its own terms — a live
                region should announce the state change concisely, not an unbounded string. */}
            {run && runState === "voided" && (
              <>
                {/* ef-036 — "· reversed {date}" is appended ONLY when this void wrote a
                    reversing entry (a posted run's void); a never-posted run's void keeps
                    the bare "Voided" text exactly as before ef-036. The date is `voidedAt`
                    itself — voiding a posted run and writing its reversal happen in the
                    SAME transaction (PayRunVoidRepository.WriteReversalVoidAsync), so
                    there is no separate "reversed at" moment to read a date from. */}
                <p className="pay-runs__voided-badge" role="status">
                  Voided
                  {run.reversalEntryId && run.voidedAt
                    ? ` · reversed ${new Date(run.voidedAt).toLocaleDateString()}`
                    : ""}
                </p>
                {run.voidReason && <p className="pay-runs__voided-reason">{run.voidReason}</p>}
              </>
            )}

            {/* ef-033 — `postedAt`/`postedEntryId` are written together, in the same
                transaction (PayRunPostRepository.WritePostAsync) — the `postedAt` guard
                is defensive only, never expected to be false while `runState` reads
                "posted". @ui-check: this is the ONE place a successful post is disclosed
                as a date, not just as the Post control disappearing. */}
            {run && runState === "posted" && run.postedAt && (
              <p className="pay-runs__posted-badge" role="status">
                Posted — {new Date(run.postedAt).toLocaleDateString()}
              </p>
            )}

            {runState === "none" && !hasApprovableHours && (
              <StateMessage
                tone="empty"
                title="No approved hours in this period yet."
                detail="Approve some time on the Payroll screen, then come back to apply it onto a run."
              />
            )}

            {runState === "none" && hasApprovableHours && (
              <p className="pay-runs__hint" role="status">
                There are approved hours in this period waiting to be applied.
              </p>
            )}

            {/*
              ALWAYS rendered once the period itself has loaded — never gated on `run`
              existing. The period picker (below) needs a stable "Pay run" table to read
              text out of for EVERY period it can land on, including one that was never
              applied: `periodReport.lines` carries one row per roster employee always
              (`HoursReportLine`'s own contract — "somebody with no approved days ... is
              still on the roster and still gets a line"), so this degrades to an
              approved-hours PREVIEW rather than disappearing. @ui-check: this is why a
              period with zero hours still renders a table, not just the empty-state
              message above it.
            */}
            <RunTable
              run={run}
              periodReport={periodReport}
              finalized={hasBeenFinalized}
              finance={financeForRun}
              onPrintCheck={
                run ? (lineId) => onNavigate(`/payroll/runs/${run.id}/print/${lineId}`) : undefined
              }
            />

            {/* ef-060-8 @ui-check: renders ONLY when the run has piece pay — no piece pay, no new DOM. */}
            {run && (
              <PayRunPieceSection summary={piece.summary} error={piece.error} finalized={hasBeenFinalized} />
            )}

            {run && runState === "applied" && missingRateEmployees.length > 0 && (
              <section className="pay-runs__rate-fixups" aria-labelledby="rate-fixups-heading">
                <h3 id="rate-fixups-heading" className="pay-runs__section-title">
                  Set a pay rate before finalizing
                </h3>
                <p className="pay-runs__rate-fixups-copy">
                  Finalize refuses the whole run rather than paying anyone $0.00 — set a
                  rate for each hourly employee below.
                </p>
                {missingRateEmployees.map((emp) => (
                  <RateFixupFieldset
                    key={emp.id}
                    employee={emp}
                    onSave={(payBasis, hourlyRate) =>
                      payRuns.updateEmployeePay(emp.id, payBasis, hourlyRate)
                    }
                  />
                ))}
              </section>
            )}

            {run && hasBeenFinalized && (
              <section
                className="pay-runs__assumptions"
                aria-labelledby="assumptions-heading"
              >
                <h3 id="assumptions-heading" className="pay-runs__section-title">
                  Assumptions
                </h3>
                <ul className="pay-runs__assumptions-list">
                  {(finalizeResult && finalizeResult.id === run.id
                    ? finalizeResult.assumptions
                    : FALLBACK_ASSUMPTIONS
                  ).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </section>
            )}
          </section>

          <HistorySection history={history} onSelect={(r) => void payRuns.showRun(r)} />
        </>
      )}
    </main>
  );
}

/**
 * @ui-check: the three lines below MUST read the same as
 * `PayRunFinalizeEndpoints.cs`'s own `Assumptions` array. They are shown ONLY as a
 * fallback, for a run finalized before this page session began (no live `POST /finalize`
 * response to read from) — a live response always wins when one is available (see
 * `financeForRun`'s sibling check above). Kept here rather than omitted: an already-
 * finalized run must still disclose what it did not compute, not just the one this
 * session happened to finalize itself.
 */
const FALLBACK_ASSUMPTIONS = [
  "State withholding is not computed by a per-state engine and is reported as $0.00 for every employee.",
  "Year-to-date wages are treated as $0.00 for every employee on this run, so the Social Security wage base and the Additional Medicare threshold are never reached.",
  "Federal withholding uses a Form W-4 held at zero: Single filing status, no dependents credit, no other income or deductions.",
];

function formatWindow(start: string, end: string): string {
  const asDate = (iso: string) => new Date(`${iso}T12:00:00Z`);
  const day = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  const dayAndYear = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
  return `${day.format(asDate(start))} – ${dayAndYear.format(asDate(end))}`;
}

interface ScheduleSectionProps {
  entityName: string;
  paySchedule: string;
  period: { start: string; end: string };
  navigating: boolean;
  onPrevious: () => void;
  onNext: () => void;
}

function ScheduleSection({
  entityName,
  paySchedule,
  period,
  navigating,
  onPrevious,
  onNext,
}: ScheduleSectionProps) {
  return (
    <section
      className="pay-runs__schedule"
      aria-labelledby="schedule-heading"
      aria-busy={navigating || undefined}
    >
      <h2 id="schedule-heading" className="pay-runs__section-title">
        Pay schedule
      </h2>
      <p className="pay-runs__schedule-line">
        {entityName} · paid <strong>{paySchedule}</strong>
      </p>
      <div className="pay-runs__period-nav">
        <button type="button" className="btn btn--ghost" onClick={onPrevious}>
          Previous period
        </button>
        <span className="pay-runs__period-label" aria-live="polite">
          {formatWindow(period.start, period.end)}
        </span>
        <button type="button" className="btn btn--ghost" onClick={onNext}>
          Next period
        </button>
      </div>
    </section>
  );
}

interface RunTableRow {
  /** ef-047-4 — this line's own id, the "Print check" action's target. Only present when
   *  `run` is non-null: a `periodReport`-only preview row has no `pay_run_line` yet. */
  lineId?: string;
  employeeId: string;
  employeeNumber: string;
  employeeName: string;
  hours: number;
  dayCount: number;
}

interface RunTableProps {
  /** The applied run for this period, or null when it has never been applied. */
  run: PayRun | null;
  /** Falls back to here when `run` is null — see the @ui-check on this component's only
   *  caller for why the table must never simply disappear. */
  periodReport: HoursReport | null;
  finalized: boolean;
  finance: PayRunFinalizeLineWithPiece[] | null;
  /** ef-047-4 — navigates to this line's print view. Undefined when `run` is null (an
   *  un-applied period has no line to print); the "Print check" cell only ever renders
   *  when this AND `finalized` are both true, gating the row action on a finalized run
   *  per the brief. */
  onPrintCheck?: (lineId: string) => void;
}

function RunTable({ run, periodReport, finalized, finance, onPrintCheck }: RunTableProps) {
  const rows: RunTableRow[] = run
    ? run.lines.map((l) => ({
        lineId: l.id,
        employeeId: l.employeeId,
        employeeNumber: l.employeeNumber,
        employeeName: l.employeeName,
        hours: l.hours,
        dayCount: l.dayCount,
      }))
    : (periodReport?.lines ?? []).map((l) => ({
        employeeId: l.employeeId,
        employeeNumber: l.employeeNumber,
        employeeName: l.employeeName,
        hours: l.approvedHours,
        dayCount: l.approvedDays,
      }));

  const showPrintColumn = finalized && !!onPrintCheck;

  const financeSum = (key: keyof Omit<PayRunFinalizeLineWithPiece, "employeeId" | "employeeNumber" | "employeeName">): string =>
    finance ? formatMoney(finance.reduce((sum, l) => sum + (l[key] ?? 0), 0)) : "—";

  // ef-060-8 @ui-check: the "Piece pay" column exists only on a finalized run that paid any piece pay —
  // a run without it keeps exactly the columns it always had.
  const showPiece = finalized && !!finance && finance.some((l) => l.piecePay > 0);

  return (
    <div className="pay-runs__table-wrap" tabIndex={0} role="group" aria-labelledby="run-heading">
      {!run && (
        <p className="pay-runs__table-caption">
          Not yet applied — hours below are approved, not yet rolled onto a run.
        </p>
      )}
      <table className="pay-runs__table ruled-rows" aria-label="Pay run">
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">Employee</th>
            <th scope="col" className="pay-runs__num">Hours</th>
            <th scope="col" className="pay-runs__num">Days</th>
            {finalized && (
              <>
                {showPiece && <th scope="col" className="pay-runs__num">Piece pay</th>}
                <th scope="col" className="pay-runs__num">Gross</th>
                <th scope="col" className="pay-runs__num">FICA (employee)</th>
                <th scope="col" className="pay-runs__num">Federal W/H</th>
                <th scope="col" className="pay-runs__num">State W/H</th>
                <th scope="col" className="pay-runs__num">Net</th>
              </>
            )}
            {/* ef-047-4 — one action per row (ADR 0009 §4), same "Print check" for
                every finalized line; never rendered on an un-applied preview row. */}
            {showPrintColumn && <th scope="col">Check</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((line) => {
            const employeeFinance = finance?.find((l) => l.employeeId === line.employeeId);
            return (
              <tr key={line.employeeId}>
                <td>{line.employeeNumber}</td>
                <th scope="row">{line.employeeName}</th>
                <td className="pay-runs__num">{formatHours(line.hours)}</td>
                <td className="pay-runs__num">{line.dayCount}</td>
                {finalized && (
                  <>
                    {showPiece && (
                      <td className="pay-runs__num">
                        {employeeFinance ? formatMoney(employeeFinance.piecePay ?? 0) : "—"}
                      </td>
                    )}
                    <td className="pay-runs__num">
                      {employeeFinance ? formatMoney(employeeFinance.gross) : "—"}
                    </td>
                    <td className="pay-runs__num">
                      {employeeFinance ? formatMoney(employeeFinance.ficaEmployee) : "—"}
                    </td>
                    <td className="pay-runs__num">
                      {employeeFinance ? formatMoney(employeeFinance.federalWh) : "—"}
                    </td>
                    <td className="pay-runs__num">
                      {employeeFinance ? formatMoney(employeeFinance.stateWh) : "—"}
                    </td>
                    <td className="pay-runs__num">
                      {employeeFinance ? formatMoney(employeeFinance.net) : "—"}
                    </td>
                  </>
                )}
                {showPrintColumn && (
                  <td>
                    {line.lineId && (
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => onPrintCheck!(line.lineId!)}
                      >
                        Print check
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
        {run && finalized && run.grossTotal !== null && run.netTotal !== null && (
          <tfoot>
            <tr>
              <td />
              <th scope="row">Total</th>
              <td className="pay-runs__num">{formatHours(run.totalHours)}</td>
              <td />
              {showPiece && <td className="pay-runs__num">{financeSum("piecePay")}</td>}
              <td className="pay-runs__num">{formatMoney(run.grossTotal)}</td>
              <td className="pay-runs__num">{financeSum("ficaEmployee")}</td>
              <td className="pay-runs__num">{financeSum("federalWh")}</td>
              <td className="pay-runs__num">{financeSum("stateWh")}</td>
              <td className="pay-runs__num">{formatMoney(run.netTotal)}</td>
              {showPrintColumn && <td />}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

interface RateFixupFieldsetProps {
  employee: PayrollEmployee;
  onSave: (payBasis: string, hourlyRate: number | null) => Promise<void>;
}

/** One employee's inline "give this person a rate" form — its own component so `useId`
 *  can label the two fields without hand-built ids per row (persona: composition over
 *  prop-drilled id strings). */
function RateFixupFieldset({ employee, onSave }: RateFixupFieldsetProps) {
  const [payBasis, setPayBasis] = useState(employee.payBasis || "hourly");
  const [hourlyRate, setHourlyRate] = useState(
    employee.hourlyRate != null ? String(employee.hourlyRate) : "",
  );
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const basisId = useId();
  const rateId = useId();

  async function handleSave(): Promise<void> {
    setFormError(null);
    const trimmed = hourlyRate.trim();
    const rate = trimmed === "" ? null : Number(trimmed);
    if (rate !== null && (Number.isNaN(rate) || rate < 0)) {
      setFormError("Enter a rate of 0 or more.");
      return;
    }
    setBusy(true);
    try {
      await onSave(payBasis, rate);
    } catch (err) {
      setFormError(problemMessage(err, "Could not save this rate."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <fieldset className="pay-runs__rate-fixup">
      <legend>Fix rate for {employee.name}</legend>

      <div className="pay-runs__field">
        <label htmlFor={basisId}>Pay basis</label>
        <select id={basisId} value={payBasis} onChange={(e) => setPayBasis(e.target.value)}>
          <option value="hourly">Hourly</option>
          <option value="salary">Salary</option>
        </select>
      </div>

      <div className="pay-runs__field">
        <label htmlFor={rateId}>Hourly rate</label>
        <input
          id={rateId}
          type="number"
          inputMode="decimal"
          min={0}
          step="0.01"
          value={hourlyRate}
          onChange={(e) => setHourlyRate(e.target.value)}
          aria-invalid={formError ? true : undefined}
        />
      </div>

      {formError && (
        <p className="pay-runs__field-error" role="alert">
          {formError}
        </p>
      )}

      {/*
        ef-037b (ADR 0009 §4) — "the pay run's action row never shows two primary actions."
        `applyIsPrimary`'s own comment covers Apply vs. Finalize/Post; this button is the
        THIRD candidate the gate actually caught: one `RateFixupFieldset` renders per
        missing-rate employee, INSIDE the same "This period's run" region as Finalize
        (`.pay-runs__rate-fixups` sits before that region's own closing tag), so a
        `.btn--primary` here coexisted with a primary Finalize on the shared demo tenant —
        `runRegion.locator(".btn--primary").count()` read 2, not because Apply and Finalize
        ever disagreed, but because THIS button was never part of that check at all. Fixing
        a rate is the real next step whenever this fieldset is on screen (Finalize 400s
        again otherwise), but it is still a correction nested inside the row, not the row's
        own primary action — ghost here, same treatment Reject/Allocate get next to Approve.
      */}
      <button
        type="button"
        className="btn btn--ghost"
        disabled={busy}
        onClick={() => void handleSave()}
      >
        {busy ? "Saving…" : "Save rate"}
      </button>
    </fieldset>
  );
}

function HistorySection({
  history,
  onSelect,
}: {
  history: PayRun[];
  onSelect: (run: PayRun) => void;
}) {
  return (
    <section className="pay-runs__history" aria-labelledby="history-heading">
      <h2 id="history-heading" className="pay-runs__section-title">
        History
      </h2>
      {history.length === 0 ? (
        <p className="pay-runs__history-empty">No pay runs yet.</p>
      ) : (
        <ul className="pay-runs__history-list" aria-labelledby="history-heading">
          {history.map((r) => (
            <li key={r.id} className="pay-runs__history-row">
              <button
                type="button"
                className="pay-runs__history-link"
                onClick={() => onSelect(r)}
              >
                <span>{formatWindow(r.periodStart, r.periodEnd)}</span>
                <span className="pay-runs__history-status">
                  {r.voidedAt
                    ? "Voided"
                    : r.finalizedAt
                      ? `Finalized${r.netTotal != null ? ` · ${formatMoney(r.netTotal)}` : ""}`
                      : "Applied"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
